'use client';

import React, { useState } from 'react';
import SEOHeading from '@/components/common/SEOHeading';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
}

const GuaranteeFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'What exactly does the 2-year health guarantee cover?',
      answer: 'Our 2-year health guarantee protects you against all hereditary and congenital defects that seriously impact your puppy\'s quality of life. This includes hip dysplasia, elbow dysplasia, heart defects, eye conditions like PRA, and genetic conditions such as EIC and CNM. If a covered condition is diagnosed, you can choose a replacement puppy, a refund, or reimbursement for treatment costs up to what you paid for your puppy.',
    },
    {
      question: 'What do I need to do within the first 14 days?',
      answer: 'Within 14 days of bringing your puppy home, you must schedule a complete veterinary examination with a licensed vet. This exam should include a thorough physical assessment, review of vaccination records, and overall health evaluation. Please send us a copy of the vet report. This required exam protects both of us by documenting your puppy\'s health status right from the start.',
    },
    {
      question: 'How do I file a claim under the health guarantee?',
      answer: 'First, get a diagnosis from your licensed veterinarian along with documentation confirming the condition is congenital or hereditary. Let us know within 7 days of the diagnosis. For serious conditions, we may ask for a second opinion from a board-certified specialist. Send us all medical records, diagnostic test results, and treatment plans. We\'ll review your claim within 10 business days and work with you to find the best solution.',
    },
    {
      question: 'What happens if my puppy develops hip dysplasia?',
      answer: 'Hip dysplasia is covered under our guarantee if diagnosed within the 2-year period and confirmed to be hereditary. You must provide OFA or PennHIP radiographs evaluated by a board-certified veterinary radiologist. If confirmed, you can choose between a replacement puppy of equal value, full refund of purchase price, or reimbursement for treatment costs up to the purchase price. We will work closely with you to ensure the best outcome for your family.',
    },
    {
      question: 'Are there any conditions that void the guarantee?',
      answer: 'Yes, the guarantee is void if the puppy is not examined by a veterinarian within 14 days, if proper care and nutrition are not provided, if the puppy is bred or used commercially, if transferred to another owner without our consent, or if conditions result from accidents, injuries, or neglect. Maintaining current vaccinations and following veterinary recommendations is essential to keep the guarantee valid.',
    },
    {
      question: 'What if I need to return the puppy for health reasons?',
      answer: 'If a serious hereditary condition is diagnosed and you choose the refund option, we require return of the puppy along with all health records and registration papers. You will receive a full refund of the purchase price (minus any non-refundable deposits). We understand this is an emotional decision and will support you throughout the process. Many families choose the replacement option to maintain the bond with a Labrador companion.',
    },
    {
      question: 'Do you provide support after the guarantee period ends?',
      answer: 'Absolutely! While the formal guarantee covers 2 years, we provide lifetime breeder support for all our puppies. We are always available to answer questions, provide guidance on health issues, training concerns, or behavioral matters. We maintain relationships with our puppy families and want to ensure your Labrador has the best possible life, regardless of whether a formal guarantee claim is involved.',
    },
    {
      question: 'How do I prove I\'ve provided proper care for my puppy?',
      answer: 'Keep detailed records of all veterinary visits, vaccinations, preventive care, and any treatments. Maintain receipts for high-quality dog food and supplements. Follow the care instructions we provide at pickup. Take photos documenting your puppy\'s living conditions and activities. These records demonstrate responsible ownership and are essential if you ever need to file a guarantee claim.',
    },
    {
      question: 'What comes with each puppy?',
      answer: 'Your puppy comes home with current vaccinations, deworming treatments, a health certificate from our veterinarian, microchip registration, starter food to help with the transition, health guarantee paperwork, and a complete care package with everything you need to know about raising your new Lab.',
    },
    {
      question: 'How do you offer delivery?',
      answer: 'We primarily offer local pickup at our facility, but we can discuss delivery options if you\'re within our service area. Delivery does come with additional fees. Just reach out and we\'ll work together to figure out what works best for your situation.',
    },
    {
      question: 'What\'s your health guarantee?',
      answer: 'Every puppy we place comes with a comprehensive health guarantee covering genetic health issues for two full years. Visit our Health Guarantee page for complete details on what\'s covered and our commitment to your puppy\'s long-term health and wellbeing.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <SEOHeading level={2} className="mb-4">
            Frequently Asked Questions About Our Health Guarantee
          </SEOHeading>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            Get answers to common questions about our health guarantee coverage, claim process, and your responsibilities as a puppy owner.
          </p>
        </div>

        <div className="mx-auto max-w-4xl space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-xl bg-card shadow-warm transition-smooth"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="flex w-full items-center justify-between gap-4 p-6 text-left transition-smooth hover:bg-muted/30"
                aria-expanded={openIndex === index}
              >
                <SEOHeading level={3} className="flex-1 text-lg">
                  {faq.question}
                </SEOHeading>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="flex-shrink-0 text-primary transition-smooth"
                />
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[500px]' : 'max-h-0'
                }`}
              >
                <div className="border-t border-border p-6 pt-4">
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="mb-6 text-muted-foreground">
            Still have questions about our health guarantee?
          </p>
          <a
            href="/contact-us"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-8 py-3 font-medium text-primary-foreground shadow-warm transition-smooth hover:scale-[0.97] hover:shadow-warm-md focus-ring"
          >
            Contact Us for More Information
            <Icon name="ArrowRightIcon" size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default GuaranteeFAQ;